import React, { useContext, useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Hidden, Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import Button from '@cpg-its/ads.common.button';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@cpg-its/ads.common.tooltip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { Beneficiary, beneficiaryContingentStatus, beneficiaryNotDesignatedStatus, beneficiaryPrimaryStatus, BeneficiaryUpdatePageModel, BeneficiaryUpdatePageOptions, BeneficiaryValidationSchema, CalculateTotalParticipation, contingentBeneficiariesTotalFieldName, ParticipationsComplete, primaryBeneficiariesTotalFieldName, shareShareAlikeSurvivorSurvivorsValue, shareShareAlikeValue } from './model/beneficiary-models';
import { BeneficiarySettings } from './model/epi-props';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { tcsmColors } from '../../../../global/constants/theme/theme-constants/colors';
import { BeneficiaryRowView } from './beneficiary-row-view';
import { TcsmStates } from '../../../../root/tcsm-model';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { FormField } from '../../../../global/components/form-element/form-field';
import { BeneficiaryRowHeader } from './beneficiary-row-header';
import { TcsmModal } from '../../../../global/components/tcsm-modal';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { formatDate } from '../../../../global/helpers/form-data-helper';
import { labelSupplementStyles } from '../../../../global/constants/theme/styles/components/label-supplement-styles';
import { benefitTableStyles } from '../../../../global/constants/theme/styles/tables/benefit-table';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { DetectBeneficiaryChanges, FormTypeByPlan, GetMappedRelationshipByValue } from './helper/beneficiary-helper';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { gridPageStyles } from '../../../../global/constants/theme/styles/layout/grid-page-layout';
import { controlledAccordionStyles } from '../../../../global/constants/theme/styles/components/controlled-accordion';
import { formInputErrorStyles } from '../../../../Global/constants/theme/styles/components/form-input-error-styles';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { postBeneficiaries } from '../../../../global/ducks/benadmin-api';
import { ArcRoutes } from '../../constants/routes';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../account-info/constants/routes';
import { setBeneficiaries } from './beneficiary-slices';
import { apiErrorClear } from '../../../../global/ducks/base-api';

interface Props {
	beneficiarySettings: BeneficiarySettings;
	beneficiaryUpdatePageModel: BeneficiaryUpdatePageModel;
	mappedRelationship: any[];
	plan: string;
	beneficiaryUpdatePageOptions: BeneficiaryUpdatePageOptions;
}

function BeneficiaryUpdateForm(props: Props) {

	const dispatch = useDispatch();

	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps?.labelSettingState?.epiContent);
	const accordionClasses = controlledAccordionStyles();
	const { beneficiarySettings, beneficiaryUpdatePageModel, mappedRelationship, plan, beneficiaryUpdatePageOptions } = props;
	const gpsItem = useGpsType(GpsType.BENEFICIARY_UPDATE);

	const app = useContext(DemographicsContext);
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);

	const { beneficiaryStatusPrimaryOptions, beneficiaryStatusContingentOptions,
		isPrimaryBeneficiaryEditableWhenMarried, planTitle, planDescription,
		shareOptionPrimaryMin, shareOptionContingentMin,
		guidelineMessage, guidelineCheckboxMessage,
		legalCheckText1, legalCheckText2, legalCheck2OnlyWhenContingent,
		hasPercentages } = beneficiaryUpdatePageOptions;

	const history = useHistory();

	const basePath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}`);

	const routeMatch = useRouteMatch();
	const { state } = useLocation<any>();
	const { beneficiaryUpdate } = state || {};
	const [isAddModalOpen, setIsAddModalOpen] = useState(beneficiaryUpdate?.isAddModalOpen || false);
	const [addToPrimary, setAddToPrimary] = useState(beneficiaryUpdate?.addToPrimary || false);
	const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(beneficiaryUpdate?.isStatusChangeModalOpen || false);
	const [statusChangeBeneficiary, setStatusChangeBeneficiary] = useState<Beneficiary>(beneficiaryUpdate?.statusChangeBeneficiary);
	const [statusChangeNewStatus, setStatusChangeNewStatus] = useState<string>(beneficiaryUpdate?.statusChangeNewStatus);
	const [expand, setExpand] = useState(beneficiaryUpdate?.expand || false);
	const [formEnabled, setFormEnabled] = useState(beneficiaryUpdate?.formEnabled || false);
	const [newBeneficiaryAdded, setNewBeneficiaryAdded] = useState(beneficiaryUpdate?.newBeneficiaryAdded || false);

	const initialValue = {
		...beneficiaryUpdatePageModel,
		primaryBeneficiariesTotal: 0,
		contingentBeneficiariesTotal: 0,
		addBeneficiariesIds: [],
		...beneficiaryUpdate?.formValues,
	};

	const apiErrors = useApiResponseError(FormTypeByPlan(plan));
	const validationSchema = BeneficiaryValidationSchema(
		labelSettings,
		beneficiarySettings,
		isPrimaryBeneficiaryEditableWhenMarried,
		legalCheckText2,
		shareOptionPrimaryMin,
		shareOptionContingentMin,
		hasPercentages,
		legalCheck2OnlyWhenContingent);

	const shareOptions = [
		{
			label: beneficiarySettings.shareShareAlikeLabel,
			value: shareShareAlikeValue,
		},
		{
			label: beneficiarySettings.shareShareAlikeSurvivorSurvivorsLabel,
			value: shareShareAlikeSurvivorSurvivorsValue,
		},
	];

	const addBeneficiariesIdsOptions: string[] = [];
	const labelClasses = labelSupplementStyles();
	const tableClasses = benefitTableStyles();
	const buttonClasses = buttonStyles();
	const pageClasses = gridPageStyles();
	const formErrorClassses = formInputErrorStyles();
	function updateFormEnabled() {
		setFormEnabled(!formEnabled);
	}

	function NavigateToCreateBeneficiary(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {
		const pushState = {
			isAddModalOpen,
			addToPrimary,
			isStatusChangeModalOpen,
			statusChangeBeneficiary,
			statusChangeNewStatus,
			expand,
			formEnabled,
			newBeneficiaryAdded,
			formValues: formikProps?.values,
		};

		history.push(`${basePath}${AccountInfoRoutes.PersonalInfoSegment}${ArcRoutes.Beneficiary}`,
			{
				beneficiaryUpdate: pushState,
				returnPath: routeMatch.path.replace(':plan', plan),
			});
	}

	function OnSubmitSuccess(response) {
		if (response?.status?.code === 'SUCCESS') {
			dispatch(setBeneficiaries(response?.data));
			history.push(`${basePath}${ArcRoutes.Beneficiary}?status=success`);
		}
	}

	function OnSubmit(submitValues: BeneficiaryUpdatePageModel) {
		dispatch(apiErrorClear());
		const updateRequest = DetectBeneficiaryChanges(initialValue, submitValues, plan, personGuid, shareOptionPrimaryMin, shareOptionContingentMin, hasPercentages);
		dispatch(postBeneficiaries(personGuid, updateRequest, OnSubmitSuccess));
	}

	function OnCancel() {
		history.goBack();
	}

	function SetPrimaryBeneficiary(formikProps: FormikProps<BeneficiaryUpdatePageModel>, newValues: Beneficiary[]) {

		// Make sure the remaining item has 100%.
		if (newValues?.length === 1 && newValues[0]?.participation !== 100) {
			formikProps.setFieldValue('primaryBeneficiaries',
				[{
					...newValues[0],
					participation: 100,
				}]);

			return;
		}

		formikProps.setFieldValue('primaryBeneficiaries', newValues);
	}

	function SetContingentBeneficiary(formikProps: FormikProps<BeneficiaryUpdatePageModel>, newValues: Beneficiary[]) {

		// Make sure the remaining item has 100%.
		if (newValues?.length === 1 && newValues[0]?.participation !== 100) {
			formikProps.setFieldValue('contingentBeneficiaries',
				[{
					...newValues[0],
					participation: 100,
				}]);

			return;
		}

		formikProps.setFieldValue('contingentBeneficiaries', newValues);
	}


	function OnChangeStatus(formikProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary, newStatus: string) {
		setIsStatusChangeModalOpen(false);
		const newBeneficiariesPrimary = [];
		const newBeneficiariesContingent = [];
		const beneficiariesPrimary = formikProps?.values?.primaryBeneficiaries;
		const beneficiariesContingent = formikProps?.values?.contingentBeneficiaries;

		if (beneficiariesPrimary?.length > 0) {
			beneficiariesPrimary?.forEach(beneficiaryItem => {
				if (beneficiaryItem?.id === beneficiary?.id) {
					if (newStatus === beneficiaryContingentStatus) {
						newBeneficiariesContingent.push({
							...beneficiaryItem,
							status: newStatus,
							participation: 0,
						});
					} else {
						newBeneficiariesPrimary.push({
							...beneficiaryItem,
							status: newStatus,
							participation: 0,
						});
					}
				} else {
					newBeneficiariesPrimary.push(
						{
							...beneficiaryItem,
							participation: 0,
						});
				}
			});
		}

		if (beneficiariesContingent?.length > 0) {
			beneficiariesContingent?.forEach(beneficiaryItem => {
				if (beneficiaryItem?.id === beneficiary?.id) {
					if (newStatus === beneficiaryPrimaryStatus) {
						newBeneficiariesPrimary.push({
							...beneficiaryItem,
							status: newStatus,
							participation: 0,
						});
					} else {
						newBeneficiariesContingent.push({
							...beneficiaryItem,
							status: newStatus,
							participation: 0,
						});
					}
				} else {
					newBeneficiariesContingent.push(
						{
							...beneficiaryItem,
							participation: 0,
						});
				}
			});
		}

		SetPrimaryBeneficiary(formikProps, newBeneficiariesPrimary);
		SetContingentBeneficiary(formikProps, newBeneficiariesContingent);
	}

	function ConfirmOnChangeStatus(formikProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary, newStatus: string) {
		if (isPrimaryBeneficiaryEditableWhenMarried || !beneficiaryUpdatePageModel.isMarried) {
			setStatusChangeBeneficiary(beneficiary);
			setStatusChangeNewStatus(newStatus);
			setIsStatusChangeModalOpen(true);

			return;
		}

		OnChangeStatus(formikProps, beneficiary, newStatus);
	}

	function RenderGuidelines() {

		return (
			<ContentSpacer>
				<div className={accordionClasses.controlledAccordionWrapper}>
					<Accordion className='controlled-accordion' expanded={expand || !formEnabled} onChange={() => setExpand(!expand)} disabled={!formEnabled}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
						>
							<h3>{beneficiarySettings?.guidelinesTitle}</h3>
						</AccordionSummary>
						<AccordionDetails >
							<WysiwygWithReactLink content={guidelineMessage} />
						</AccordionDetails>
					</Accordion>
					<div className='MuiAccordion-root-checkbox'>
						<FormControlLabel
							control={
								<Checkbox
									checked={formEnabled}
									onChange={() => updateFormEnabled()}
									name="formEnabled"
									required
								/>
							}
							label={guidelineCheckboxMessage}
						/>
					</div>
				</div>

			</ContentSpacer>);
	}

	function IsSpouseDesignatedAndOnlyOne(formikProps: FormikProps<BeneficiaryUpdatePageModel>): boolean {
		if (isPrimaryBeneficiaryEditableWhenMarried || !beneficiaryUpdatePageModel.isMarried) return true;

		const primaryBeneficiaries = formikProps?.values?.primaryBeneficiaries?.filter(beneficiary => beneficiary?.status === beneficiaryPrimaryStatus);
		const primaryCount = primaryBeneficiaries?.length;
		const spouseCount = primaryBeneficiaries?.filter(beneficiary => beneficiary?.status === beneficiaryPrimaryStatus && beneficiary?.isCurrentSpouse)?.length;

		const initialPrimaryBeneficiary = initialValue?.primaryBeneficiaries;
		const initialBeneficiaryCount = initialValue?.primaryBeneficiaries?.length;

		let countBeneficiariesOnInitialValue = 0;
		primaryBeneficiaries.forEach(
			beneficiary => {
				if (initialPrimaryBeneficiary?.find(initialBeneficiary => initialBeneficiary.id === beneficiary.id)) {
					countBeneficiariesOnInitialValue += 1;
				}
			});

		return (spouseCount === 1) &&
			(primaryCount === 1 || (countBeneficiariesOnInitialValue === initialBeneficiaryCount && primaryCount === initialBeneficiaryCount));
	}

	function IsSpouseDesignated(formikProps: FormikProps<BeneficiaryUpdatePageModel>): boolean {
		if (isPrimaryBeneficiaryEditableWhenMarried || !beneficiaryUpdatePageModel.isMarried) return true;

		const primaryBeneficiaries = formikProps?.values?.primaryBeneficiaries;
		const spouseCount = primaryBeneficiaries?.filter(beneficiary => beneficiary?.status === beneficiaryPrimaryStatus && beneficiary?.isCurrentSpouse)?.length;

		return (spouseCount === 1);
	}

	function RenderDesignatedMessage(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {
		if (!IsSpouseDesignatedAndOnlyOne(formikProps)) {
			return (
				<ContentSpacer>
					<Banner banner={{
						type: 'error',
						label: <WysiwygWithReactLink content={beneficiarySettings?.notlegallyDesignatedWarningTitle} />,
						description: <WysiwygWithReactLink content={beneficiarySettings?.notlegallyDesignatedWarningDescription} />,
					}} />
				</ContentSpacer>);
		}

		return null;
	}

	function RemovePrimary(formikProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary) {
		const newBeneficiariesPrimary = [];
		const beneficiariesPrimary = formikProps?.values?.primaryBeneficiaries;

		if (beneficiariesPrimary?.length > 0) {
			beneficiariesPrimary?.forEach(beneficiaryItem => {
				if (beneficiaryItem?.id !== beneficiary?.id) {
					newBeneficiariesPrimary.push(beneficiaryItem);
				}
			});
		}

		SetPrimaryBeneficiary(formikProps, newBeneficiariesPrimary);
	}

	function RemoveContingent(formikProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary) {
		const newBeneficiariesContingent = [];
		const beneficiariesContingent = formikProps?.values?.contingentBeneficiaries;

		if (beneficiariesContingent?.length > 0) {
			beneficiariesContingent?.forEach(beneficiaryItem => {
				if (beneficiaryItem?.id !== beneficiary?.id) {
					newBeneficiariesContingent.push(beneficiaryItem);
				}
			});
		}

		SetContingentBeneficiary(formikProps, newBeneficiariesContingent);
	}

	function HasPercentageMissingValues(formikProps: FormikProps<BeneficiaryUpdatePageModel>, fieldName: string): boolean {
		const error: string = formikProps?.errors[fieldName];
		return error?.length > 0;
	}

	function RenderCountError(formikProps: FormikProps<BeneficiaryUpdatePageModel>, fieldName: string) {
		if (HasPercentageMissingValues(formikProps, fieldName)) {
			return (
				<div className={formErrorClassses.errorWrapper}>
					<div className='input-error right-arrow' data-formdata={fieldName} > {formikProps?.errors[fieldName]} </div>
				</div>
			);
		}

		return null;
	}

	function RenderPercentageTotal(label: string, separator: string, allocated: number) {
		return (
			<p><strong>{`${label || ''} ${allocated || '0'}% ${separator || ''} 100%`}</strong></p>
		);
	}

	function RenderPrimaryPercentageTotal(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {
		if (!isPrimaryBeneficiaryEditableWhenMarried) return null;

		return RenderPercentageTotal(
			beneficiarySettings?.allocatedPercentageLabel,
			beneficiarySettings?.allocatedPercentageSeparatorLabel,
			CalculateTotalParticipation(formikProps?.values?.primaryBeneficiaries, beneficiaryPrimaryStatus));
	}

	function RenderContingentPercentageTotal(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {

		return RenderPercentageTotal(
			beneficiarySettings?.allocatedPercentageLabel,
			beneficiarySettings?.allocatedPercentageSeparatorLabel,
			CalculateTotalParticipation(formikProps?.values?.contingentBeneficiaries, beneficiaryContingentStatus));
	}

	function OpenAddPrimaryModal(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {

		formikProps?.setFieldValue('addBeneficiariesIds', []);

		setAddToPrimary(true);
		setIsAddModalOpen(true);
	}

	function OpenAddContingentModal(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {
		formikProps?.setFieldValue('addBeneficiariesIds', []);

		setAddToPrimary(false);
		setIsAddModalOpen(true);
	}

	function CloseAddModal() {
		setIsAddModalOpen(false);
		setNewBeneficiaryAdded(false);
	}

	function CloseStatusChangeModal() {
		setIsStatusChangeModalOpen(false);
		setStatusChangeBeneficiary(null);
		setStatusChangeNewStatus(null);
		setNewBeneficiaryAdded(false);
	}

	function PushBeneficiaries(formikProps: FormikProps<BeneficiaryUpdatePageModel>, newBeneficiaries: Beneficiary[], fieldName: string) {
		if (newBeneficiaries?.length > 0) {

			const mergedBeneficiaries = [];
			const beneficiaries = formikProps?.values[fieldName];

			mergedBeneficiaries.push(...beneficiaries);
			mergedBeneficiaries.push(...newBeneficiaries);

			formikProps.setFieldValue(fieldName, mergedBeneficiaries);
		}
	}

	function AddBeneficiaries(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {

		if (formikProps?.values?.addBeneficiariesIds?.length > 0) {
			const newBeneficiaries = [];
			const clientBeneficiaries = beneficiaryUpdatePageModel?.clientBeneficiaries;

			const itemsCount = addToPrimary ? formikProps?.values?.primaryBeneficiaries.length : formikProps?.values?.contingentBeneficiaries.length;

			formikProps?.values?.addBeneficiariesIds.forEach(id => {
				const beneficiaryItem = clientBeneficiaries.find(beneficiary => beneficiary?.id === id);
				if (beneficiaryItem) {
					let beneficiaryNewStatus = addToPrimary ? beneficiaryPrimaryStatus : beneficiaryContingentStatus;

					const primaryInitialValue = initialValue?.primaryBeneficiaries?.find(item => item.id === beneficiaryItem?.id);

					if (addToPrimary && !isPrimaryBeneficiaryEditableWhenMarried && beneficiaryUpdatePageModel?.isMarried && beneficiaryItem?.isCurrentSpouse) {

						beneficiaryNewStatus = primaryInitialValue?.status === beneficiaryPrimaryStatus ? beneficiaryPrimaryStatus : beneficiaryNotDesignatedStatus;
					}

					newBeneficiaries.push({
						...beneficiaryItem,
						status: beneficiaryNewStatus,
						participation: itemsCount === 0 ? 100 : null,
					});
				}
			});

			PushBeneficiaries(formikProps, newBeneficiaries, addToPrimary ? 'primaryBeneficiaries' : 'contingentBeneficiaries');
		}

		CloseAddModal();
	}

	function RenderAddBeneficiaryRowHeader() {
		return (
			<Hidden xsDown>
				<div className='benefit-table-row'>
					<div className='benefit-table-row-cell benefit-table-row-cell-checkbox'>
						<p className='input-label'>{beneficiarySettings?.addColumnLabel}</p>
					</div>
					<div className='benefit-table-row-cell'>
						<p className='input-label'>{beneficiarySettings?.nameColumnLabel}</p>
					</div>
					<div className='benefit-table-row-cell'>
						<p className='input-label'>{beneficiarySettings?.dateOfBirthColumnLabel}</p>
					</div>
					<div className='benefit-table-row-cell'>
						<p className='input-label'>{beneficiarySettings?.relationshipColumnLabel}</p>
					</div>
				</div>
			</Hidden>);
	}

	function OnAddBeneficiariesChange(e, fieldArrayProps: FieldArrayRenderProps) {
		const { value } = e.target;
		if (e.target.checked) {
			addBeneficiariesIdsOptions.push(value);
			fieldArrayProps.push(value);
		} else {
			const itemIndex = addBeneficiariesIdsOptions.indexOf(value);
			if (itemIndex > -1) {
				fieldArrayProps.remove(itemIndex);
				addBeneficiariesIdsOptions.splice(itemIndex, 1);
			}
		}
	}

	function IsBeneficiaryNotSelected(formProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiary: Beneficiary): boolean {
		if (!beneficiary?.id) return true;

		const primaryBeneficiary = formProps?.values?.primaryBeneficiaries?.find(beneficiaryItem => beneficiaryItem?.id === beneficiary.id);
		const contingentBeneficiary = formProps?.values?.contingentBeneficiaries?.find(beneficiaryItem => beneficiaryItem?.id === beneficiary.id);

		return !primaryBeneficiary && !contingentBeneficiary;
	}

	function IsSubmitDisabled(formProps: FormikProps<BeneficiaryUpdatePageModel>): boolean {
		if (legalCheckText2 && (!legalCheck2OnlyWhenContingent || (legalCheck2OnlyWhenContingent && formProps?.values.contingentBeneficiaries?.length > 0))) {
			return !formProps?.values?.legalCheck || !formProps?.values?.legalCheck2 || !formEnabled || !IsSpouseDesignatedAndOnlyOne(formProps);
		}

		return !formProps?.values?.legalCheck || !formEnabled || !IsSpouseDesignatedAndOnlyOne(formProps);
	}

	function RenderAddBeneficiaryRow(formProps: FormikProps<BeneficiaryUpdatePageModel>, fieldArrayProps: FieldArrayRenderProps, beneficiary: Beneficiary) {
		const isElegible = !beneficiary?.ineligibleFor?.includes(plan);

		return (
			<>
				<div className={`benefit-table-row ${isElegible ? '' : 'benefit-table-row-disabled'}`} key={beneficiary?.id}>
					<div className='benefit-table-row-cell benefit-table-row-cell-checkbox'>
						<div>
							<FormField
								id={beneficiary.id}
								name='addBeneficiariesIds'
								component='checkBox'
								touched={formProps.touched}
								errors={formProps.errors}
								value={beneficiary.id}
								checked={formProps.values.addBeneficiariesIds?.includes(beneficiary.id)}
								disabled={!isElegible}
								checkBoxGroup
								onChange={(e) => OnAddBeneficiariesChange(e, fieldArrayProps)}
							/>
						</div>
					</div>
					<div className='benefit-table-row-cell'>
						<Hidden smUp>
							<p className='input-label'>{beneficiarySettings?.nameColumnLabel}</p>
						</Hidden>
						<p>
							{beneficiary.firstName} {beneficiary.lastName}
						</p>
					</div>
					<div className='benefit-table-row-cell'>
						<Hidden smUp>
							<p className='input-label'>{beneficiarySettings?.dateOfBirthColumnLabel}</p>
						</Hidden>
						<p>
							{beneficiary.dob ? formatDate(beneficiary.dob, true, true) : ''}
						</p>
					</div>
					<div className='benefit-table-row-cell'>
						<Hidden smUp>
							<p className='input-label'>{beneficiarySettings?.relationshipColumnLabel}</p>
						</Hidden>
						<div>
							{GetMappedRelationshipByValue(beneficiary.relationship, mappedRelationship)}
						</div>
					</div>
				</div>
			</>
		);
	}

	function RenderBeneficiaryAddedSucess() {
		if (!newBeneficiaryAdded) return null;

		return (
			<ContentSpacer>
				<Banner banner={{
					type: 'success',
					label: <WysiwygWithReactLink content={beneficiarySettings?.changeSavedMessage} />,
				}} />
			</ContentSpacer>
		);
	}

	function RenderAddBeneficiary(formProps: FormikProps<BeneficiaryUpdatePageModel>) {
		const remainingBeneficiaries = beneficiaryUpdatePageModel?.clientBeneficiaries?.filter(beneficiary => IsBeneficiaryNotSelected(formProps, beneficiary));
		if (remainingBeneficiaries?.length > 0) {
			return (
				<DividerSection removeDivider>
					<ContentSpacer>
						<h3>{addToPrimary ? beneficiarySettings?.addPrimaryBeneficiaryTitle : beneficiarySettings?.addContingentBeneficiaryTitle}</h3>
					</ContentSpacer>
					<div className={`${tableClasses.tableWrapper} left-aligned-cells`}>
						<RenderAddBeneficiaryRowHeader />
						<FieldArray
							name='addBeneficiariesIds'
							render={(fieldArrayProps) => (
								<>
									<div className='quickfind-scroll-container'>
										{remainingBeneficiaries?.map((beneficiary) => RenderAddBeneficiaryRow(formProps, fieldArrayProps, beneficiary))}
									</div>
								</>
							)}
						/>
						<div className='benefit-table-helper benefit-table-helper-row'>
							<div className='benefit-table-helper-row-inner'>
								<button className={buttonClasses.secondaryButton} type='button' disabled={formProps?.values?.addBeneficiariesIds?.length === 0} onClick={() => AddBeneficiaries(formProps)}>{beneficiarySettings?.addNewBeneficiaryButtonText}</button>
							</div>
						</div>
					</div>

				</DividerSection>
			);
		}

		return (
			<DividerSection removeDivider sectionText={addToPrimary ? beneficiarySettings?.addPrimaryBeneficiaryTitle : beneficiarySettings?.addContingentBeneficiaryTitle}>
				{beneficiarySettings?.addBeneficiaryEmptyMessage}
			</DividerSection>
		);
	}

	function RenderCreateBeneficiary(formikProps: FormikProps<BeneficiaryUpdatePageModel>) {
		return (
			<DividerSection removeDivider>
				<ContentSpacer>
					<h3>{beneficiarySettings?.createBeneficiaryTitle}</h3>
				</ContentSpacer>
				<button className={buttonClasses.secondaryButton} type='button' onClick={() => NavigateToCreateBeneficiary(formikProps)}>{beneficiarySettings?.createNewBeneficiaryButtonText}</button>
			</DividerSection>
		);
	}

	function RenderShareSection(formProps: FormikProps<BeneficiaryUpdatePageModel>, beneficiariesFieldName: string, optionFieldName: string, shareOptionsMin: number) {
		if (shareOptionsMin === -1) return null;

		const beneficiaries = formProps?.values[beneficiariesFieldName];

		if (beneficiaries?.length >= shareOptionsMin) {
			return (
				<DividerSection>
					<ContentSpacer>
						<FormField
							name={optionFieldName}
							label=''
							component="radioGroup"
							options={shareOptions}
							touched={formProps.touched}
							errors={formProps.errors}
							required
						/>
					</ContentSpacer>
					<WysiwygWithReactLink content={beneficiarySettings?.shareShareAlikeDescription} />
				</DividerSection>
			);
		}

		return null;
	}

	function RenderLegalAcknowledgement(formProps: FormikProps<BeneficiaryUpdatePageModel>) {
		return (
			<DividerSection removeDivider sectionText={beneficiarySettings?.legalAcknowledgementTitle}>
				<FormField
					name='legalCheck'
					label={legalCheckText1}
					component='checkBox'
					touched={formProps.touched}
					errors={formProps.errors}
					value={formProps?.values?.legalCheck ?? false}
					checked={formProps?.values?.legalCheck ?? false}
					required
				/>
				{legalCheckText2 && (!legalCheck2OnlyWhenContingent || (legalCheck2OnlyWhenContingent && formProps?.values.contingentBeneficiaries?.length > 0)) &&
					<FormField
						name='legalCheck2'
						label={legalCheckText2}
						component='checkBox'
						touched={formProps.touched}
						errors={formProps.errors}
						value={formProps?.values?.legalCheck2 ?? false}
						checked={formProps?.values?.legalCheck2 ?? false}
						required
					/>}
			</DividerSection>
		);
	}

	function RenderPrimaryBeneficiaryNotEditableMessage() {
		if (!isPrimaryBeneficiaryEditableWhenMarried && beneficiaryUpdatePageModel.isMarried) {
			return (
				<>
					<div className={labelClasses.labelLayout}>
						<h3>{beneficiarySettings?.primaryBeneficiaryNotEditableTitle}</h3>
						<div className='supplement-wrapper'>
							<Tooltip iconColor={tcsmColors.blue} title={beneficiarySettings?.primaryBeneficiaryNotEditableHelperText} />
						</div>
					</div>
					<WysiwygWithReactLink content={beneficiarySettings?.primaryBeneficiaryNotEditableMessage} />
				</>
			);
		}

		return null;
	}

	function IsPrimaryBeneficaryStatusEditable(beneficiary: Beneficiary) {
		const primaryInitialValue = initialValue?.primaryBeneficiaries?.find(item => item.id === beneficiary?.id);

		return isPrimaryBeneficiaryEditableWhenMarried ||
			!beneficiaryUpdatePageModel?.isMarried ||
			(!primaryInitialValue?.status && beneficiary?.isCurrentSpouse) ||
			(primaryInitialValue?.status === beneficiaryNotDesignatedStatus);
	}

	function IsContingetBeneficaryStatusEditable() {
		return isPrimaryBeneficiaryEditableWhenMarried || !beneficiaryUpdatePageModel.isMarried;
	}

	return (
		<PageContainer>
			<MainContent fullWidth>
				<div className={pageClasses.gridPageContainer}>
					<div className='grid-main-content'>
						<PageHeader
							headerText={planTitle}
							description={planDescription}
						/>

						<RenderGuidelines />
					</div>

					<div className='grid-sidebar'>
						<SideBarContent>
							<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
						</SideBarContent>
					</div>

					<Formik
						initialValues={initialValue}
						onSubmit={(values) => OnSubmit(values)}
						validationSchema={validationSchema}
						enableReinitialize
						initialErrors={apiErrors}
					>
						{(formProps: FormikProps<BeneficiaryUpdatePageModel>) => (
							<Form className='grid-fw-content grid-nested'>
								<FormWrapper
									formikProps={formProps}
									submitText={beneficiarySettings?.submitButtonText}
									cancelText={beneficiarySettings?.backButton}
									submitDisabled={IsSubmitDisabled(formProps)}
									onCancel={() => OnCancel()}
									preventButtonDisableValidation
								>
									<div className='grid-fw-content'>
										<DividerSection disabledOverlay={!formEnabled} removeDivider>
											<div className='grid-fw-content grid-nested'>
												<div className='grid-main-content'>
													<ContentSpacer>
														<h2>{beneficiarySettings?.beneficiaryUpdatePageTitle}</h2>
														<WysiwygWithReactLink content={beneficiarySettings?.beneficiaryUpdatePageDescription} />
													</ContentSpacer>

													<div className={labelClasses.labelLayout}>
														<h3>{beneficiarySettings?.updatePrimaryBeneficiaryTitle}</h3>
														<div className='supplement-wrapper'>
															<Tooltip iconColor={tcsmColors.blue} title={beneficiarySettings?.primaryBeneficiaryTitleHelperText} />							</div>
													</div>

													{
														formProps?.values?.primaryBeneficiaries?.length === 0 &&
														<>
															<ContentSpacer>
																<WysiwygWithReactLink content={beneficiarySettings?.noneSelectedText} />
															</ContentSpacer>
															<ContentSpacer>
																<button className={buttonClasses.secondaryButton} type='button' onClick={() => OpenAddPrimaryModal(formProps)}>{beneficiarySettings?.addCreateNewBeneficiaryButtonText}</button>
															</ContentSpacer>
														</>
													}
												</div>

												<div className='grid-fw-content'>
													{
														formProps?.values?.primaryBeneficiaries?.length > 0 &&
														<ContentSpacer>
															<div className={tableClasses.tableWrapper}>
																<BeneficiaryRowHeader beneficiarySettings={beneficiarySettings} hasPercentages={hasPercentages} />
																<>
																	{
																		formProps?.values?.primaryBeneficiaries?.map((beneficiary, beneficiaryIndex) =>
																			<BeneficiaryRowView key={`${beneficiary.id}-${beneficiaryIndex}`} fieldName='primaryBeneficiaries' beneficiary={beneficiary} beneficiarySettings={beneficiarySettings} formikProps={formProps}
																				index={beneficiaryIndex} labelSetting={labelSettings} beneficiaryStatusOptions={beneficiaryStatusPrimaryOptions}
																				isUniquePrimary={formProps?.values?.primaryBeneficiaries?.length === 1}
																				isStatusEditable={IsPrimaryBeneficaryStatusEditable(beneficiary)}
																				statusEditableOnlyForNotDesignated={!isPrimaryBeneficiaryEditableWhenMarried && beneficiaryUpdatePageModel?.isMarried}
																				hasPercentageMissingValues={!ParticipationsComplete(formProps?.values?.primaryBeneficiaries, beneficiaryPrimaryStatus)} isRemovable
																				hasIncorrectTotal={HasPercentageMissingValues(formProps, primaryBeneficiariesTotalFieldName)} hasPercentages={hasPercentages}
																				mappedRelationship={mappedRelationship}
																				onChangeStatus={ConfirmOnChangeStatus}
																				onRemove={RemovePrimary} />,
																		)
																	}
																</>
																<div className='benefit-table-helper benefit-table-helper-row'>
																	<div className='benefit-table-helper-row-inner'>
																		<button className={buttonClasses.secondaryButton} type='button' onClick={() => OpenAddPrimaryModal(formProps)}>{beneficiarySettings?.addCreateNewBeneficiaryButtonText}</button>
																		{
																			hasPercentages &&
																			<div className='benefit-table-helper-text'>
																				<div>
																					{RenderPrimaryPercentageTotal(formProps)}
																					{RenderCountError(formProps, primaryBeneficiariesTotalFieldName)}
																				</div>
																			</div>
																		}
																	</div>
																</div>
															</div> </ContentSpacer>
													}

													{RenderDesignatedMessage(formProps)}
												</div>

												<div className='grid-main-content'>

													{RenderShareSection(formProps, 'primaryBeneficiaries', 'primaryShareOption', shareOptionPrimaryMin)}
													<ContentSpacer>
														{RenderPrimaryBeneficiaryNotEditableMessage()}
													</ContentSpacer>
												</div>
											</div>
										</DividerSection>
										<DividerSection disabledOverlay={!formEnabled || !IsSpouseDesignated(formProps)} removeDivider>
											<div className={labelClasses.labelLayout}>
												<h3>{beneficiarySettings?.updateContingentBeneficiaryTitle}</h3>
												<div className='supplement-wrapper'>
													<Tooltip iconColor={tcsmColors.blue} title={beneficiarySettings?.contingentBeneficiaryHelperText} />
												</div>
											</div>
											{
												formProps?.values?.contingentBeneficiaries?.length === 0 &&
												<>
													<ContentSpacer>
														<WysiwygWithReactLink content={beneficiarySettings?.noneSelectedText} />
													</ContentSpacer>
													<ContentSpacer>
														<button className={buttonClasses.secondaryButton} type='button' onClick={() => OpenAddContingentModal(formProps)}>{beneficiarySettings?.addCreateNewBeneficiaryButtonText}</button>
													</ContentSpacer>
												</>
											}

											{formProps?.values?.contingentBeneficiaries?.length > 0 &&
												<div className='grid-fw-content'>
													<ContentSpacer>
														<div className={tableClasses.tableWrapper}>
															<BeneficiaryRowHeader beneficiarySettings={beneficiarySettings} hasPercentages={hasPercentages} />
															<>
																{
																	formProps?.values?.contingentBeneficiaries?.map((beneficiary, beneficiaryIndex) =>
																		<BeneficiaryRowView key={`${beneficiary.id}-${beneficiaryIndex}`} fieldName='contingentBeneficiaries' beneficiary={beneficiary} beneficiarySettings={beneficiarySettings} formikProps={formProps}
																			index={beneficiaryIndex} labelSetting={labelSettings} beneficiaryStatusOptions={beneficiaryStatusContingentOptions}
																			isStatusEditable={IsContingetBeneficaryStatusEditable()} mappedRelationship={mappedRelationship} isRemovable
																			hasPercentageMissingValues={!ParticipationsComplete(formProps?.values?.contingentBeneficiaries, beneficiaryContingentStatus)}
																			hasIncorrectTotal={HasPercentageMissingValues(formProps, contingentBeneficiariesTotalFieldName)} hasPercentages={hasPercentages}
																			onChangeStatus={ConfirmOnChangeStatus} onRemove={RemoveContingent} />,
																	)
																}
															</>
															<div className='benefit-table-helper benefit-table-helper-row'>
																<div className='benefit-table-helper-row-inner'>
																	<button className={buttonClasses.secondaryButton} type='button' onClick={() => OpenAddContingentModal(formProps)}>{beneficiarySettings?.addCreateNewBeneficiaryButtonText}</button>
																	{
																		hasPercentages &&
																		<div className='benefit-table-helper-text'>
																			<div>
																				{RenderContingentPercentageTotal(formProps)}
																				{RenderCountError(formProps, contingentBeneficiariesTotalFieldName)}
																			</div>
																		</div>
																	}
																</div>
															</div>
														</div>
													</ContentSpacer>
												</div>}
											{RenderShareSection(formProps, 'contingentBeneficiaries', 'contingentShareOption', shareOptionContingentMin)}
										</DividerSection>
										<div className='grid-main-content'>
											{RenderLegalAcknowledgement(formProps)}
										</div>
									</div>
								</FormWrapper>
								<TcsmModal open={isAddModalOpen} handleClose={CloseAddModal}>
									{RenderBeneficiaryAddedSucess()}
									{RenderAddBeneficiary(formProps)}
									{RenderCreateBeneficiary(formProps)}
								</TcsmModal>
								<TcsmModal open={isStatusChangeModalOpen} handleClose={CloseStatusChangeModal}>
									<>
										<ContentSpacer>
											<WysiwygWithReactLink content={beneficiarySettings?.statusChangeFormModalMessage} />
										</ContentSpacer>
										<ButtonWrapper flexEnd>
											<Button type='button' buttonStyle='tertiary' text={beneficiarySettings?.statusChangeFormModalCancelText} onClick={CloseStatusChangeModal} />
											<Button type='button' buttonStyle='primary' text={beneficiarySettings?.statusChangeFormModalConfirmText} onClick={() => OnChangeStatus(formProps, statusChangeBeneficiary, statusChangeNewStatus)} />
										</ButtonWrapper>
									</>

								</TcsmModal>
							</Form>
						)}
					</Formik >
					{/*  </div>*/}
				</div>
			</MainContent>
		</PageContainer>
	);
}

export default BeneficiaryUpdateForm;